/* eslint-disable vue/no-v-html */
<template>
  <div class="main-content">
    <div>
      <b-row class="mb-16">
        <b-col md="8">
          <h2 class="page-title">
            Time Logs
          </h2>
        </b-col>
        <b-col md="4">
          <b-form-input
            v-model="searchWord"
            class="form-control-rounded"
            type="text"
            required
            placeholder="Search..."
          >
          </b-form-input>
        </b-col>
      </b-row>
    </div>
    <b-row>
      <b-col md="12" class="filter-container">
        <b-form-group label="Date Range" class="date">
          <v-date-picker
            v-model="dateRange"
            :first-day-of-week="1"
            mode="range"
            :input-props="{
              placeholder: 'Please select date range'
            }"
            :masks="{ input: 'YYYY/MM/DD' }"
          ></v-date-picker>
        </b-form-group>
        <b-form-group label="Client" class="type">
          <b-form-select
            v-model="client"
            :options="options"
            value-field="value"
            text-field="text"
            class="mb-3 select-custom"
          >
            <template v-slot:first>
              <option :value="null" disabled
                >-- Please select a Client --</option
              >
            </template>
          </b-form-select>
        </b-form-group>
        <b-button
          pill
          variant="outline-primary ripple"
          class="clear-btn"
          size="sm"
          @click="clear"
          >Clear</b-button
        >
      </b-col>
    </b-row>
    <b-row>
      <overflowMessage ref-name="timelogTable" />
      <b-table
        ref="timelogTable"
        v-model="visibleRows"
        :busy="loading"
        sticky-header
        thead-class="cursor-pointer"
        responsive
        striped
        hover
        bordered
        :items="filteredUsers"
        :fields="filteredFields"
        class="time-table"
      >
        <template v-slot:table-busy>
          <TableLoadingSkeleton />
        </template>
        <template v-slot:cell()="row">
          <span v-html="getCellContents(row)"> </span>
        </template>
        <!-- <template v-slot:head()>
          <th class="no-border"></th>
        </template> -->
        <template slot="bottom-row">
          <td
            v-for="field in getUniqueFields()"
            :key="field"
            v-html="getClientTotal(field)"
          ></td>
        </template>
      </b-table>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex"
import momentBusiness from "moment-business-days"
import overflowMessage from "@/components/misc/overflowMessage"
import TableLoadingSkeleton from "@/components/misc/tableLoadingSkeleton"

export default {
  name: "TimeLogs",
  components: {
    overflowMessage,
    TableLoadingSkeleton
  },
  apollo: {
    clients: {
      query: require("@/graphql/Client/GetClients.gql"),
      update(data) {
        data.clients
          .sort((a, b) => (a?.name > b?.name ? 1 : -1))
          .forEach(client => {
            this.options.push({ value: client.id, text: client.name })
          })
        return data.clients
      }
    },
    timeLogs: {
      query: require("@/graphql/TimeLog/GetTimeLogs.gql"),
      variables() {
        return {
          filterModel: {
            from: this.dateRange.start
          }
        }
      },
      update(data) {
        setTimeout(() => {
          this.loading = false
        }, 0)
        return data.getTimeLogs?.filter(x => x.user)
      }
    }
  },
  data() {
    return {
      loading: true,
      options: [],
      client: null,
      searchWord: null,
      dateRange: {
        start: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate(),
        end: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate()
      },
      visibleRows: []
    }
  },
  computed: {
    ...mapState({
      users: state =>
        state.users.all.filter(x => !x.id.includesLowerCase("unassigned"))
    }),
    filteredFields() {
      return [].concat(
        ...[{ key: "" }, { key: "Total", sortable: true }],
        this.timeLogs
          ?.filter(x => !this.client || x.client.id == this.client)
          ?.map(x => {
            return { key: x.client.name, sortable: false }
          }) ?? []
      )
    },
    filteredUsers() {
      let users =
        this.timeLogs
          ?.map(x => {
            return { userName: this.$utils.getUserName(x.user), id: x.user.id }
          })
          .filter(
            x =>
              //Search bar filter
              !this.searchWord || x.userName.includesLowerCase(this.searchWord)
          ) ?? []

      //Distinct array of TimeLogUsers with userName and id
      const timeLogUsers = [...new Set(users.map(x => x.id))].map(x => {
        return {
          id: x,
          userName: users.find(u => u.id == x).userName
        }
      })

      //Return all users - timelog users in front of array
      return [
        ...timeLogUsers,
        ...this.users
          //Filter out users who are in timeLogUsers Array
          .filter(
            u =>
              !timeLogUsers.find(x => x.id == u.id) &&
              //Search bar filter
              (!this.searchWord ||
                u.firstName.includesLowerCase(this.searchWord))
          )
          //Just want name and Id
          .map(x => {
            return {
              id: x.id,
              userName: this.$utils.getUserName(x)
            }
          })
      ]
    }
  },
  watch: {
    dateRange() {
      //Once the dateRange has been set the state
      //..of the view can be set to ready
      this.loading = true
    }
  },
  created() {},
  methods: {
    /**
     * Clears form data
     */
    clear() {
      this.searchWord = null
      this.client = null
      this.dateRange = {
        start: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate(),
        end: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate()
      }
    },
    /**
     * Gets data for cell
     * @param row
     * @returns {number|string|string|*}
     */
    getCellContents(row) {
      if (row.field.key.includesLowerCase("key"))
        return `<strong>${row.item.userName}</strong>`
      if (row.field.key.includesLowerCase("total"))
        return this.getUserTotal(row.item) || "-"
      return parseFloat(this.getTotalForClient(row.item, row.field.key)) || "-"
    },
    /**
     * Gets total time logged for user
     * @param user
     * @returns {string}
     */
    getUserTotal(user) {
      if (this.timeLogs?.some(x => x))
        return this.timeLogs
          .reduce((total, timelog) => {
            if (this.$utils.isObject(total)) total = 0
            total += timelog.user.id == user.id ? timelog.time : 0
            return total
          })
          .toFixed(2)
      else return 0
    },
    /**
     * Gets total time logged for client
     * @param clientName
     * @returns {string}
     */
    getClientTotal(clientName) {
      let total = 0
      if (!clientName) return ""
      //Filter by client name and search bar
      let toReduce =
        this.timeLogs
          ?.filter(
            x =>
              (clientName == "Total" || x.client.name == clientName) &&
              (!this.searchWord ||
                x.user.firstName.includesLowerCase(this.searchWord))
          )
          ?.map(x => x.time) ?? []
      //Can't reduce empty array
      //...Reduce if not empty
      if (toReduce.length) total = toReduce.reduce((a, c) => a + c) ?? 0
      //Bold
      return `<strong>${total.toFixed(2)}</strong>`
    },
    /**
     * Gets total time logged for client by user
     * @param user
     * @param clientName
     * @returns {string}
     */
    getTotalForClient(user, clientName) {
      return this.timeLogs
        .reduce((total, timelog) => {
          if (this.$utils.isObject(total)) total = 0
          total +=
            timelog.user.id == user.id && timelog.client.name == clientName
              ? timelog.time
              : 0
          return total
        })
        .toFixed(2)
    },
    /**
     * Returns unique fields
     * @returns {*[]}
     */
    getUniqueFields() {
      return [...new Set(this.filteredFields.map(x => x.key))]
    },
    /**
     * Indicates whether or not the table's x axis is overflowing
     * @returns {boolean}
     */
    tableIsOverflowing() {
      let el = this.$refs.timelogTable?.$el
      if (!el) return false
      let curOverflow = el.style.overflow

      if (!curOverflow || curOverflow === "visible")
        el.style.overflow = "hidden"

      let isOverflowing =
        el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight

      el.style.overflow = curOverflow

      return isOverflowing
    }
  }
}
</script>

<style scoped>
.filter-container {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding: 0;
  margin: 0;
}
.clear-btn {
  height: 2rem;
  margin: auto 0 auto 0;
}
.type {
  flex: 0.4;
}
.date {
  flex: 0.3;
}
.no-border {
  border: 0;
}

.loader {
  border: 2px solid #f3f3f3;
  border-radius: 50%;
  border-top: 2px solid #3498db;
  width: 50px;
  height: 50px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;
  left: 50%;
  top: 25%;
  overflow: hidden !important;
  margin: auto !important;
  position: absolute;
}

.table-loading {
  opacity: 0.5 !important;
}

/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.time-table {
  margin-bottom: 2rem;
}
</style>

<style>
.table.b-table > thead > tr > th {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
}
div::-webkit-scrollbar {
  margin-top: 0 !important;
  height: 0.3rem;
}

div::-webkit-scrollbar-track {
  background: lightslategray;
}

div::-webkit-scrollbar-thumb {
  background: #3f51b5;
}
</style>
